import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { findInsurance, type Provider } from "@/data/heallexa";
import { Heart, Search, User as UserIcon, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { HeroSearch } from "@/components/landing/HeroSearch";
import { StickyPageHeader, PageBody } from "@/components/dashboard/DashboardShell";
import {
 loadPatientDashboardFn,
 saveProfileFn,
 removeFavoriteFn,
 cancelAppointmentFn,
 rescheduleAppointmentFn,
} from "./-dashboard.patient.functions";

export const Route = createFileRoute("/dashboard/patient")({
 validateSearch: (s: Record<string, unknown>) => ({
 tab: typeof s.tab === "string" ? s.tab : undefined,
 }),
 component: PatientDashboard,
});

interface ProfileRow {
 id: string; display_name: string | null; handle: string | null; avatar_url: string | null;
}

function PatientDashboard() {
 const { user, loading } = useAuth();
 const navigate = useNavigate();
 const search = useSearch({ from: "/dashboard/patient" });
 const [profile, setProfile] = useState<ProfileRow | null>(null);
 const [favorites, setFavorites] = useState<Provider[]>([]);
 const [searches, setSearches] = useState<{ id: string; query: string; created_at: string }[]>([]);
 const [requests, setRequests] = useState<any[]>([]);
 const [appointments, setAppointments] = useState<any[]>([]);
 const [savingProfile, setSavingProfile] = useState(false);
 const validTabs = ["overview", "favorites", "searches", "requests", "profile"] as const;
 type TabKey = typeof validTabs[number];
 const tab: TabKey = (validTabs as readonly string[]).includes(search.tab ?? "")
 ? (search.tab as TabKey)
 : "overview";
 const setTab = (t: TabKey) =>
 navigate({ to: "/dashboard/patient", search: { tab: t === "overview" ? undefined : t } as any, replace: true });

 const reload = async () => {
 if (!user) return;
 const { profile: prof, favorites: favs, searches: hist, requests: reqs, appointments: appts } =
 await loadPatientDashboardFn();
 setProfile(prof ?? { id: user.id, display_name: "", handle: "", avatar_url: "" });
 setFavorites((favs ?? []) as Provider[]);
 setSearches(hist ?? []);
 setRequests(reqs ?? []);
 setAppointments(appts ?? []);
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 reload();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user, loading]);

 const saveProfile = async () => {
 if (!user || !profile) return;
 setSavingProfile(true);
 try {
 await saveProfileFn({ data: {
 display_name: profile.display_name ?? "",
 handle: profile.handle ?? "",
 avatar_url: profile.avatar_url ?? "",
 } });
 toast.success("Profile saved");
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to save profile");
 } finally {
 setSavingProfile(false);
 }
 };

 const removeFavorite = async (providerId: string) => {
 if (!user) return;
 await removeFavoriteFn({ data: { providerId } });
 setFavorites((f) => f.filter((p) => p.id !== providerId));
 };

 const cancelAppointment = async (id: string) => {
 if (!confirm("Cancel this appointment?")) return;
 try {
 await cancelAppointmentFn({ data: { id } });
 } catch (e: any) { toast.error(e?.message ?? "Failed to cancel"); return; }
 toast.success("Appointment canceled");
 setAppointments((list) => list.map((a) => (a.id === id ? { ...a, status: "canceled" } : a)));
 };

 const rescheduleAppointment = async (a: any) => {
 const current = new Date(a.appointment_at);
 const pad = (n: number) => String(n).padStart(2, "0");
 const def = `${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}T${pad(current.getHours())}:${pad(current.getMinutes())}`;
 const input = prompt("Reschedule to (YYYY-MM-DDTHH:MM, local time):", def);
 if (!input) return;
 const next = new Date(input);
 if (Number.isNaN(next.getTime())) { toast.error("Invalid date/time"); return; }
 if (next < new Date()) { toast.error("Pick a future time"); return; }
 try {
 await rescheduleAppointmentFn({ data: {
 id: a.id,
 previousAt: a.appointment_at,
 nextAt: next.toISOString(),
 } });
 } catch (e: any) { toast.error(e?.message ?? "Failed to reschedule"); return; }
 toast.success("Appointment rescheduled, confirmation email sent");
 setAppointments((list) => list.map((x) => (x.id === a.id ? { ...x, appointment_at: next.toISOString(), status: "booked" } : x)));
 };
 if (loading || !user) return <FullLoader />;

 return (
 <div className="min-h-full bg-muted/30">

 <StickyPageHeader
   icon={<UserIcon className="h-4 w-4 text-primary" />}
   title={`Welcome back${profile?.display_name ? `, ${profile.display_name}` : ""}`}
   subtitle={user.email}
 />

 <PageBody className="space-y-10">
 <section>
 <h2 className="text-[15px] font-medium mb-2 flex items-center gap-2"><Search className="h-4 w-4 text-primary" /> Find a provider</h2>
 <p className="text-[13px] text-muted-foreground mb-2">Search by specialty, location, insurance, or ask in plain language.</p>
 <div className="-mt-2"><HeroSearch /></div>
 </section>

 {tab === "overview" && (
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <Stat label="Upcoming appointments" value={appointments.filter((a) => a.status !== "canceled" && new Date(a.appointment_at) >= new Date()).length} />
 <Stat label="Saved providers" value={favorites.length} />
 <Stat label="Recent searches" value={searches.length} />
 <Stat label="Account" value="Patient" />
 </div>
 )}

 {tab === "requests" && (
 <div className="space-y-8">
 <Section title="My appointments" icon={<CalendarClock className="h-4 w-4" />}>
 {appointments.length === 0 ? (
 <Empty cta={<Button asChild><Link to="/browse">Find a provider</Link></Button>}>
 No appointments yet. Pick a date on a provider's profile to book one.
 </Empty>
 ) : (
 <ul className="card-surface-flat rounded-md overflow-hidden divide-y divide-border">
 {appointments.map((a) => {
 const when = new Date(a.appointment_at);
 const past = when < new Date();
 const canCancel = !past && a.status !== "canceled";
 return (
 <li key={a.id} className="px-4 py-3 flex flex-wrap justify-between items-center gap-2">
 <div className="min-w-0">
 <p className="font-medium text-sm truncate">
 <Link to="/providers/$id" params={{ id: a.listing_id }} className="hover:text-primary">
 {a.providers?.practice ?? a.providers?.name ?? "Provider"}
 </Link>
 </p>
 <p className="text-xs text-muted-foreground">
 {when.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
 {a.service_requested ? ` · ${a.service_requested}` : ""}
 </p>
 </div>
 <div className="flex items-center gap-2">
 <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${
 a.status === "confirmed" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" :
 a.status === "canceled" ? "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20" :
 "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20"
 }`}>{a.status}</span>
 {canCancel && (
 <>
 <Button size="sm" variant="outline" onClick={() => rescheduleAppointment(a)}>Reschedule</Button>
 <Button size="sm" variant="outline" onClick={() => cancelAppointment(a.id)}>Cancel</Button>
 </>
 )}
 </div>
 </li>
 );
 })}
 </ul>
 )}
 </Section>

 {requests.length > 0 && (
 <Section title="AI assistant requests" icon={<CalendarClock className="h-4 w-4" />}>
 <ul className="card-surface-flat rounded-md overflow-hidden divide-y divide-border">
 {requests.map((r) => (
 <li key={r.id} className="px-4 py-3 flex flex-wrap justify-between items-center gap-2">
 <div>
 <p className="font-medium text-sm">{r.providers?.practice ?? r.providers?.name ?? "Provider"}</p>
 <p className="text-xs text-muted-foreground">
 {r.proposed_slot ? new Date(r.proposed_slot).toLocaleString() : "Time flexible"}
 {r.reason_for_visit ? ` · ${r.reason_for_visit}` : ""}
 </p>
 </div>
 <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${
 r.status === "confirmed" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" :
 r.status === "declined" || r.status === "canceled" ? "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20" :
 "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20"
 }`}>{r.status}</span>
 </li>
 ))}
 </ul>
 </Section>
 )}
 </div>
 )}

 {tab === "favorites" && (
 <Section title="Saved providers" icon={<Heart className="h-4 w-4" />}>
 {favorites.length === 0 ? (
 <Empty cta={<Button asChild><Link to="/browse">Browse providers</Link></Button>}>No saved providers yet.</Empty>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {favorites.map((p) => (
 <div key={p.id} className="card-surface-flat hover-lift rounded-md p-4">
 <div className="flex items-start justify-between gap-3">
 <Link to="/providers/$id" params={{ id: p.id }} className="block flex-1 hover:text-primary">
 <p className="font-semibold text-sm">{p.name}</p>
 <p className="text-xs text-muted-foreground">{p.practice} · {p.city}</p>
 </Link>
 <button className="text-xs text-muted-foreground hover:text-destructive" onClick={() => removeFavorite(p.id)}>Remove</button>
 </div>
 <div className="mt-2 flex flex-wrap gap-1">
 {p.insurance.slice(0, 4).map((id) => {
 const ins = findInsurance(id); if (!ins) return null;
 return <span key={id} className="inline-flex h-6 items-center rounded-sm border border-border bg-background px-2 text-[11px] leading-none text-muted-foreground">{ins.short}</span>;
 })}
 </div>
 </div>
 ))}
 </div>
 )}
 </Section>
 )}

 {tab === "searches" && (
 <Section title="Recent searches" icon={<Search className="h-4 w-4" />}>
 {searches.length === 0 ? <Empty>No recent searches.</Empty> : (
 <ul className="card-surface-flat rounded-md overflow-hidden divide-y divide-border">
 {searches.map((s) => (
 <li key={s.id} className="px-4 py-3 flex justify-between items-center">
 <span className="text-sm">{s.query}</span>
 <span className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</span>
 </li>
 ))}
 </ul>
 )}
 </Section>
 )}

 {tab === "profile" && profile && (
 <Section title="Account profile" icon={<UserIcon className="h-4 w-4" />}>
 <div className="card-surface p-6 space-y-4 max-w-xl">
 <div className="space-y-1.5">
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Display name</Label>
 <Input value={profile.display_name ?? ""} onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} />
 </div>
 <div className="space-y-1.5">
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Handle</Label>
 <Input value={profile.handle ?? ""} onChange={(e) => setProfile({ ...profile, handle: e.target.value })} />
 </div>
 <div className="space-y-1.5">
 <Label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Email</Label>
 <Input value={user.email ?? ""} disabled />
 </div>
 <Button onClick={saveProfile} disabled={savingProfile}>
 {savingProfile && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
 </Button>
 </div>
 </Section>
 )}
 </PageBody>
 </div>
 );
}

function Section({ title, icon, children }: any) {
 return <section><h2 className="flex items-center gap-2 text-[15px] font-medium mb-4 [&_svg]:text-primary">{icon}{title}</h2>{children}</section>;
}
function Empty({ children, cta }: { children: React.ReactNode; cta?: React.ReactNode }) {
 return <div className="card-surface p-10 text-center rounded-md"><p className="text-muted-foreground text-[14px]">{children}</p>{cta && <div className="mt-4">{cta}</div>}</div>;
}
function Stat({ label, value }: { label: string; value: string | number }) {
 return <div className="card-surface p-4 sm:p-5 rounded-md"><p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground truncate">{label}</p><p className="text-[28px] font-medium tracking-tight mt-3">{value}</p></div>;
}
function FullLoader() {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
}
